import {
  AnalyticsPageRouting,
  AnalyticsProvider,
  useTrackHideIntercomWidget,
  useTrackShowIntercomWidget,
} from '@air/marketing-analytics';
import { ChilipiperScript } from '@air/plasmic-chilipiper';
import { NextSeo, NextSeoProps } from 'next-seo';
import { HTMLProps, useEffect } from 'react';

import { DefaultFooter } from '~/old-marketing/components/globals/footers/DefaultFooter';
import { DefaultHeader } from '~/old-marketing/components/globals/headers/DefaultHeader/DefaultHeader';
import { GlobalStyle } from '~/old-marketing/components/GlobalStyle';
import { PreviewModeBanner } from '~/old-marketing/components/PreviewModeBanner';
import { PreviewModeProvider } from '~/providers/PreviewModeProvider';
import { GoogleTagManager } from '~/scripts/GoogleTagManager';

export interface MarketingLayoutProps extends HTMLProps<HTMLElement> {
  layout?: {
    enableIntercom: boolean;
    header: {
      appearance?: 'dark' | 'light';
      hasAuthNavigation?: boolean;
      hasMobileNavigation?: boolean;
      hasDemoOnlyCTANavigation?: boolean;
      hasNavigation?: boolean;
      isFixed?: boolean;
      isHiddenOnSticky?: boolean;
      variant?: 'dark' | 'light';
    };
    noindex?: boolean;
    id?: string;
    preview: boolean;
    seo: NextSeoProps;
  };
}

export const MarketingLayout = ({ layout, children }: MarketingLayoutProps) => {
  const { trackShowIntercomWidget } = useTrackShowIntercomWidget();
  const { trackHideIntercomWidget } = useTrackHideIntercomWidget();

  useEffect(() => {
    if (layout?.enableIntercom) {
      trackShowIntercomWidget();
    } else {
      trackHideIntercomWidget();
    }
  }, [layout?.enableIntercom, trackShowIntercomWidget, trackHideIntercomWidget]);

  return (
    <AnalyticsProvider>
      <AnalyticsPageRouting />
      <PreviewModeProvider value={{ isPreview: layout?.preview ?? false }}>
        <>
          <GlobalStyle />

          <GoogleTagManager />
          <ChilipiperScript />

          <NextSeo {...layout?.seo} noindex={layout?.noindex} titleTemplate="%s" />
          {layout?.preview && <PreviewModeBanner id={layout?.id} />}
          <DefaultHeader
            appearance={layout?.header?.appearance ?? layout?.header?.variant}
            hasAuthNavigation={layout?.header?.hasAuthNavigation}
            hasDemoOnlyCTANavigation={layout?.header?.hasDemoOnlyCTANavigation}
            hasMobileNavigation={layout?.header?.hasMobileNavigation}
            hasNavigation={layout?.header?.hasNavigation}
            isFixed={layout?.header?.isFixed}
            isHiddenOnSticky={layout?.header?.isHiddenOnSticky}
          />
          {children}
          <DefaultFooter />
        </>
      </PreviewModeProvider>
    </AnalyticsProvider>
  );
};
